import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { authFetch } from "../api";

export default function UsersPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [approvedFilter, setApprovedFilter] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const limit = 20;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    loadUsers();
  }, [navigate, page, search, roleFilter, approvedFilter]);

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append("search", search);
      if (roleFilter) params.append("role", roleFilter);
      if (approvedFilter !== "") params.append("approved", approvedFilter);

      const data = await authFetch(`/admin/users?${params}`);
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      console.error("Error loading users:", err);
      if (err.message.includes("403") || err.message.includes("Admin")) {
        setError("You do not have permission to view this page. Admin access required.");
      } else {
        setError("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await authFetch(`/admin/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      setMessage({ type: "success", text: "User updated successfully" });
      loadUsers();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (err) {
      console.error("Error updating user:", err);
      setMessage({ type: "error", text: "Failed to update user" });
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete user "${email}"? This cannot be undone.`)) {
      return;
    }

    try {
      await authFetch(`/admin/users/${userId}`, {
        method: "DELETE",
      });
      setMessage({ type: "success", text: "User deleted successfully" });
      loadUsers();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (err) {
      console.error("Error deleting user:", err);
      setMessage({ type: "error", text: err.message || "Failed to delete user" });
    }
  };

  const totalPages = Math.ceil(total / limit);

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  };

  const containerStyle = {
    maxWidth: "1200px",
    margin: "40px auto",
    padding: "0 20px",
  };

  const sectionStyle = {
    backgroundColor: "white",
    padding: "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "24px",
    borderBottom: "2px solid #000",
    paddingBottom: "12px",
  };

  const inputStyle = {
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
  };

  const selectStyle = {
    ...inputStyle,
    minWidth: "140px",
  };

  const buttonStyle = {
    padding: "10px 20px",
    backgroundColor: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
  };

  if (loading && users.length === 0) {
    return (
      <div style={pageStyle}>
        <Header showLogout={true} />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: "18px", color: "#666" }}>Loading users...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <Header showLogout={true} />
        <div style={containerStyle}>
          <div style={{
            padding: "20px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            borderRadius: "8px",
            textAlign: "center",
          }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <Header showLogout={true} />

      <div style={containerStyle}>
        {/* Message Display */}
        {message.text && (
          <div style={{
            padding: "12px 16px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: message.type === "success" ? "#d4edda" : "#f8d7da",
            color: message.type === "success" ? "#155724" : "#721c24",
            border: `1px solid ${message.type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
          }}>
            {message.text}
          </div>
        )}

        <div style={sectionStyle}>
          <h2 style={headingStyle}>User Management</h2>

          {/* Filters */}
          <form onSubmit={handleSearch} style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: "200px" }}
            />
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              style={selectStyle}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="inventory">Inventory</option>
              <option value="sales">Sales</option>
            </select>
            <select
              value={approvedFilter}
              onChange={(e) => { setApprovedFilter(e.target.value); setPage(1); }}
              style={selectStyle}
            >
              <option value="">All Status</option>
              <option value="true">Approved</option>
              <option value="false">Pending</option>
            </select>
            <button type="submit" style={buttonStyle}>Search</button>
          </form>

          {/* Users Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #ddd" }}>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        {user.profile_picture_url ? (
                          <img
                            src={user.profile_picture_url}
                            alt=""
                            style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                          />
                        ) : (
                          <div style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            color: "#666",
                          }}>
                            {user.email.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span>{user.email}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {user.first_name || user.last_name
                        ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                        : "-"}
                    </td>
                    <td style={tdStyle}>
                      <select
                        value={user.role}
                        onChange={(e) => handleUpdateUser(user.id, { role: e.target.value })}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          fontSize: "13px",
                          backgroundColor: user.role === "admin" ? "#f3e5f5" : user.role === "inventory" ? "#e3f2fd" : "#f5f5f5",
                        }}
                      >
                        <option value="sales">Sales</option>
                        <option value="inventory">Inventory</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleUpdateUser(user.id, { approved: !user.approved })}
                        style={{
                          padding: "4px 12px",
                          borderRadius: "4px",
                          border: "none",
                          fontSize: "12px",
                          fontWeight: "500",
                          cursor: "pointer",
                          backgroundColor: user.approved ? "#d4edda" : "#fff3cd",
                          color: user.approved ? "#155724" : "#856404",
                        }}
                      >
                        {user.approved ? "Approved" : "Pending"}
                      </button>
                    </td>
                    <td style={tdStyle}>
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #dc3545",
                          backgroundColor: "transparent",
                          color: "#dc3545",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "24px" }}>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                style={{
                  ...buttonStyle,
                  backgroundColor: page === 1 ? "#ccc" : "#000",
                  cursor: page === 1 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: "14px", color: "#666" }}>
                Page {page} of {totalPages} ({total} users)
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                style={{
                  ...buttonStyle,
                  backgroundColor: page === totalPages ? "#ccc" : "#000",
                  cursor: page === totalPages ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "12px 8px",
  fontSize: "13px",
  fontWeight: "600",
  color: "#333",
};

const tdStyle = {
  padding: "12px 8px",
  fontSize: "14px",
};
